import React from 'react';
import clsx from 'clsx';
import { Checkbox, CheckboxField } from '@oysterjs/ui/checkbox';
import { Field as UIField, Label } from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { Select } from '@oysterjs/ui/select';
import { Switch, SwitchField } from '@oysterjs/ui/switch';
import { Textarea } from '@oysterjs/ui/textarea';
import {
  BrandLogoState,
  BrandMarginsState,
  BrandPalette,
  BrandSlotLogoState,
  BrandSlotsState,
  BrandVoiceState,
  LOGO_VARIANTS,
  slotLogoKey,
  ELEMENTS,
  ELEMENT_LABELS,
  ElementStyle,
  MARGIN_SIDES,
  SLOT_POSITIONS,
  shows,
  findElement,
  roleColor,
  sameElement,
  setElement,
  partOn,
  COLOR_ROLES,
  KEEP_LINES,
  LINE_HEIGHTS,
  MARGIN_STEPS,
  SIZE_STEPS,
  SPACE_STEPS
} from './design-brand';

export type TypefaceOption = {
  family: string;
  metricAlias?: string | null;
  /** The font-family the stylesheet emits, so the preview is set in what the print will be. */
  cssStack: string;
};

export const LogoDraftContext = React.createContext<{
  stage: (file: File) => string;
  url: (mark: BrandLogoState) => string | undefined;
} | null>(null);

export const IconAction = ({
  label,
  disabled,
  onClick,
  children
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    disabled={disabled}
    onClick={onClick}
    className="shrink-0 rounded-lg p-1.5 text-neutral-600 hover:bg-neutral-100 disabled:opacity-30 disabled:hover:bg-transparent dark:text-neutral-400 dark:hover:bg-white/10"
  >
    {children}
  </button>
);

/** What a running header or footer prints, by position. */
export const SlotFields = ({
  which,
  slots,
  logos,
  onChange
}: {
  which: 'header' | 'footer';
  slots: BrandSlotsState | null;
  logos: BrandLogoState[];
  onChange: (slots: BrandSlotsState | null) => void;
}) => {
  const set = (patch: Partial<BrandSlotsState>) => onChange({ ...(slots ?? {}), ...patch });
  const filled = SLOT_POSITIONS.some(
    ({ key }) => slots?.[key] != null || slots?.[slotLogoKey(key)] != null
  );
  const on = partOn(slots);

  return (
    <section>
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-medium text-neutral-900 dark:text-white">
          {which === 'header' ? 'Header content' : 'Footer content'}
        </h3>
        <button
          type="button"
          disabled={!on || !filled}
          onClick={() => onChange(slots?.enabled == null ? null : { enabled: slots.enabled })}
          className="text-xs font-medium text-sky-600 hover:underline disabled:text-neutral-300 disabled:no-underline dark:text-sky-400 dark:disabled:text-neutral-600"
        >
          Clear
        </button>
      </div>

      <PartSwitch checked={on} onChange={(v) => set({ enabled: v })} />

      {SLOT_POSITIONS.map(({ key, label }) => (
        <Field key={key} label={label}>
          <Choice
            value={slots?.[slotLogoKey(key)]?.variant ?? ''}
            disabled={!on}
            onChange={(v) =>
              set({
                [slotLogoKey(key)]: v
                  ? {
                      variant: v as BrandSlotLogoState['variant'],
                      maxHeightPt: slots?.[slotLogoKey(key)]?.maxHeightPt ?? null
                    }
                  : null
              })
            }
            options={[
              { value: '', label: 'Text' },
              ...LOGO_VARIANTS.map((v) => ({
                value: v.key,
                label: `Logo, ${v.label.toLowerCase()}`
              }))
            ]}
          />
          {slots?.[slotLogoKey(key)] && logos.length === 0 && (
            <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-400">
              No logo uploaded yet. Add one on the Brand tab.
            </p>
          )}
          {slots?.[slotLogoKey(key)] && (
            <Input
              className="mt-1"
              type="number"
              min={8}
              max={72}
              step={1}
              aria-label={`${label} logo height (pt)`}
              placeholder="Height (pt)"
              value={slots[slotLogoKey(key)]?.maxHeightPt ?? ''}
              disabled={!on}
              onChange={(e) =>
                set({
                  [slotLogoKey(key)]: {
                    variant: slots[slotLogoKey(key)]!.variant,
                    maxHeightPt: e.target.value === '' ? null : Number(e.target.value)
                  }
                })
              }
            />
          )}
          {!slots?.[slotLogoKey(key)] && (
            <Input
              className="mt-1"
              type="text"
              value={slots?.[key] ?? ''}
              disabled={!on}
              onChange={(e) => set({ [key]: e.target.value === '' ? null : e.target.value })}
              placeholder="Empty"
            />
          )}
        </Field>
      ))}

      <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
        {which === 'footer'
          ? 'Write {page}, {pages} or {date} and the value is filled in. Leave the right corner empty and the page number goes there as "2 of 5".'
          : 'Write {page}, {pages} or {date} and the value is filled in.'}
      </p>
    </section>
  );
};

/** Page margins. */
export const MarginFields = ({
  margins,
  onChange
}: {
  margins: BrandMarginsState | null;
  onChange: (margins: BrandMarginsState | null) => void;
}) => {
  const set = (patch: Partial<BrandMarginsState>) => onChange({ ...(margins ?? {}), ...patch });
  const changed = MARGIN_SIDES.some(({ key }) => margins?.[key] != null);
  const on = partOn(margins);

  return (
    <section>
      <div className="flex items-baseline justify-between gap-2">
        <div>
          <h3 className="text-sm font-medium text-neutral-900 dark:text-white">Margins</h3>
          <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
            Switched off, documents keep their template’s margins.
          </p>
        </div>
        <button
          type="button"
          disabled={!on || !changed}
          onClick={() => onChange(margins?.enabled == null ? null : { enabled: margins.enabled })}
          className="text-xs font-medium text-sky-600 hover:underline disabled:text-neutral-300 disabled:no-underline dark:text-sky-400 dark:disabled:text-neutral-600"
        >
          Reset
        </button>
      </div>

      <PartSwitch checked={on} onChange={(v) => set({ enabled: v })} />

      <div className="mt-2 grid grid-cols-2 gap-2">
        {MARGIN_SIDES.map(({ key, label }) => (
          <Field key={key} label={label}>
            <Choice
              value={margins?.[key] ?? ''}
              disabled={!on}
              onChange={(v) => set({ [key]: v === '' ? null : Number(v) })}
              options={[
                { value: '', label: 'Default' },
                ...MARGIN_STEPS.map((inches) => ({ value: inches, label: `${inches}\u2033` }))
              ]}
            />
          </Field>
        ))}
      </div>
      <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
        Inches, including space for the header and footer.
      </p>
    </section>
  );
};

export const ElementFields = ({
  element,
  onFocus,
  elements,
  defaults,
  typefaces,
  palette,
  headingTypeface,
  bodyTypeface,
  disabled,
  onChange
}: {
  element: ElementStyle['element'];
  onFocus?: () => void;
  elements: ElementStyle[];
  defaults: ElementStyle[];
  typefaces: TypefaceOption[];
  palette: BrandPalette;
  headingTypeface: string;
  bodyTypeface: string;
  /** The part this element belongs to is switched off, so its type is kept but not applied. */
  disabled?: boolean;
  onChange: (elements: ElementStyle[]) => void;
}) => {
  const style = findElement(elements, element);
  const fallback = findElement(defaults, element);
  const set = (patch: Partial<ElementStyle>) =>
    onChange(setElement(elements, { ...style, ...patch }));

  const inherits = element.startsWith('HEADING') ? headingTypeface : bodyTypeface;
  const changed = !sameElement(style, fallback);
  const fillRole =
    element === ELEMENTS.tableStripe && !elements.some((each) => each.element === element)
      ? 'PRIMARY'
      : style.fillRole;

  return (
    <section onFocusCapture={onFocus} onPointerDown={onFocus}>
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-medium text-neutral-900 dark:text-white">
          {ELEMENT_LABELS[element] ?? element}
        </h3>
        <button
          type="button"
          disabled={disabled || !changed}
          onClick={() => onChange(setElement(elements, fallback))}
          className="text-xs font-medium text-sky-600 hover:underline disabled:text-neutral-300 disabled:no-underline dark:text-sky-400 dark:disabled:text-neutral-600"
        >
          Reset
        </button>
      </div>

      {shows(element, 'fontFamily') && (
        <Field label="Font family">
          <Choice
            value={style.fontFamily ?? ''}
            disabled={disabled}
            onChange={(v) => set({ fontFamily: v || null })}
            options={[
              { value: '', label: `Follows the brand (${inherits})` },
              ...typefaces.map((t) => ({ value: t.family, label: t.family }))
            ]}
          />
        </Field>
      )}

      <div className="mt-2 grid grid-cols-2 gap-2">
        {shows(element, 'size') && (
          <Field label="Size (pt)">
            <Choice
              value={style.sizePt ?? ''}
              disabled={disabled}
              onChange={(v) => set({ sizePt: v === '' ? null : Number(v) })}
              options={[
                { value: '', label: 'Default' },
                ...SIZE_STEPS.map((pt) => ({ value: pt, label: `${pt}` }))
              ]}
            />
          </Field>
        )}
        {shows(element, 'lineHeight') && (
          <Field label="Line height">
            <Choice
              value={style.lineHeight ?? ''}
              disabled={disabled}
              onChange={(v) => set({ lineHeight: v === '' ? null : Number(v) })}
              options={[
                { value: '', label: 'Default' },
                ...LINE_HEIGHTS.map((n) => ({ value: n, label: `${n}` }))
              ]}
            />
          </Field>
        )}
      </div>

      {shows(element, 'space') && (
        <div className="mt-2 grid grid-cols-2 gap-2">
          <Field label="Space above (pt)">
            <Choice
              value={style.spaceAbovePt ?? ''}
              disabled={disabled}
              onChange={(v) => set({ spaceAbovePt: v === '' ? null : Number(v) })}
              options={[
                { value: '', label: 'Default' },
                ...SPACE_STEPS.map((pt) => ({ value: pt, label: `${pt}` }))
              ]}
            />
          </Field>
          <Field label="Space below (pt)">
            <Choice
              value={style.spaceBelowPt ?? ''}
              disabled={disabled}
              onChange={(v) => set({ spaceBelowPt: v === '' ? null : Number(v) })}
              options={[
                { value: '', label: 'Default' },
                ...SPACE_STEPS.map((pt) => ({ value: pt, label: `${pt}` }))
              ]}
            />
          </Field>
        </div>
      )}

      {shows(element, 'color') && (
        <Field label="Color">
          <Choice
            value={style.colorRole ?? ''}
            disabled={disabled}
            onChange={(v) => set({ colorRole: (v || null) as ElementStyle['colorRole'] })}
            options={[
              { value: '', label: 'Default' },
              ...COLOR_ROLES.map((role) => ({
                value: role,
                label: role.charAt(0) + role.slice(1).toLowerCase()
              }))
            ]}
          />
        </Field>
      )}

      {shows(element, 'fill') && (
        <Field label="Fill">
          <div className="flex items-center gap-2">
            <Choice
              value={fillRole ?? ''}
              disabled={disabled}
              onChange={(v) => set({ fillRole: (v || null) as ElementStyle['fillRole'] })}
              options={[
                { value: '', label: 'None' },
                ...COLOR_ROLES.map((role) => ({
                  value: role,
                  label: role.charAt(0) + role.slice(1).toLowerCase()
                }))
              ]}
            />
            {element === ELEMENTS.tableStripe && (
              <span
                aria-hidden
                data-testid="preview-stripe-swatch"
                title="Stripe color before tinting"
                className={clsx(
                  'size-8 shrink-0 rounded-md border border-neutral-300 dark:border-neutral-600',
                  !fillRole && 'border-dashed'
                )}
                style={{ background: roleColor(fillRole, palette) ?? undefined }}
              />
            )}
          </div>
        </Field>
      )}

      {shows(element, 'linesKept') && (
        <>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <Field label="Lines kept at bottom">
              <Choice
                value={style.linesKeptAtBottom ?? ''}
                disabled={disabled}
                onChange={(v) => set({ linesKeptAtBottom: v === '' ? null : Number(v) })}
                options={[
                  { value: '', label: 'Default' },
                  ...KEEP_LINES.map((n) => ({ value: n, label: `${n}` }))
                ]}
              />
            </Field>
            <Field label="Lines kept at top">
              <Choice
                value={style.linesKeptAtTop ?? ''}
                disabled={disabled}
                onChange={(v) => set({ linesKeptAtTop: v === '' ? null : Number(v) })}
                options={[
                  { value: '', label: 'Default' },
                  ...KEEP_LINES.map((n) => ({ value: n, label: `${n}` }))
                ]}
              />
            </Field>
          </div>
          <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
            Fewest lines left at a page break.
          </p>
        </>
      )}

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
        {shows(element, 'weight') && (
          <>
            <Toggle
              label="Bold"
              checked={!!style.bold}
              disabled={disabled}
              onChange={(v) => set({ bold: v })}
            />
            <Toggle
              label="Italic"
              checked={!!style.italic}
              disabled={disabled}
              onChange={(v) => set({ italic: v })}
            />
          </>
        )}
        {shows(element, 'pageBreakBefore') && (
          <Toggle
            label="Start on a new page"
            checked={!!style.pageBreakBefore}
            disabled={disabled}
            onChange={(v) => set({ pageBreakBefore: v })}
          />
        )}
      </div>
    </section>
  );
};

export const LogoImage = ({
  mark,
  alt,
  testId,
  height
}: {
  mark: BrandLogoState;
  alt: string;
  testId?: string;
  height?: number;
}) => {
  const drafts = React.useContext(LogoDraftContext);
  return (
    <img
      src={drafts ? drafts.url(mark) : (mark.url ?? undefined)}
      alt={alt}
      title={mark.name}
      data-testid={testId}
      className={height ? undefined : 'max-h-12 max-w-full'}
      style={height ? { height, width: 'auto', display: 'inline-block' } : undefined}
    />
  );
};

/** Mirrors brand.MaxLogoBytes. */
const MAX_LOGO_BYTES = 5 * 1024 * 1024;

/** A subset of the types brand.UsableLogo takes, which also allows JPEG and WebP. */
const LOGO_TYPES = ['image/png', 'image/svg+xml'];

export const LogoFields = ({
  logos,
  onChange
}: {
  logos: BrandLogoState[];
  onChange: (logos: BrandLogoState[]) => void;
}) => {
  const drafts = React.useContext(LogoDraftContext)!;
  const [error, setError] = React.useState<string | null>(null);
  const choose = (variant: BrandLogoState['variant'], file: File) => {
    if (!LOGO_TYPES.includes(file.type) || file.size > MAX_LOGO_BYTES) {
      setError(`Choose a PNG or SVG image up to ${MAX_LOGO_BYTES / (1024 * 1024)}\u00a0MB.`);
      return;
    }
    setError(null);
    onChange([
      ...logos.filter((logo) => logo.variant !== variant),
      { fileId: drafts.stage(file), variant }
    ]);
  };

  return (
    <section>
      <h3 className="text-sm font-medium text-neutral-900 dark:text-white">Logos</h3>
      <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
        One for light pages, one for dark. PNG or SVG.
      </p>
      <div className="mt-2 grid grid-cols-2 gap-2">
        {LOGO_VARIANTS.map((variant) => {
          const mark = logos.find((l) => l.variant === variant.key);
          return (
            <div
              key={variant.key}
              className="rounded-md border border-neutral-200 p-2 dark:border-neutral-700"
            >
              <div className="text-xs font-medium text-neutral-900 dark:text-white">
                {variant.label}
              </div>
              <div
                className={clsx(
                  'mt-1 flex h-16 items-center justify-center rounded border',
                  !mark && 'border-dashed',
                  // dark-mode-exempt: each slot is the page its logo prints on, in either theme.
                  variant.key === 'LIGHT' && 'border-neutral-300 bg-neutral-50',
                  variant.key === 'DARK' && 'border-neutral-500 bg-neutral-900'
                )}
              >
                {mark ? (
                  <LogoImage mark={mark} alt={variant.label} />
                ) : (
                  <span
                    className={clsx(
                      'px-2 text-center text-[11px]',
                      // dark-mode-exempt: the swatch background is fixed per variant, not per theme.
                      variant.key === 'DARK' ? 'text-neutral-400' : 'text-neutral-500'
                    )}
                  >
                    {variant.key === 'DARK' && logos.some((logo) => logo.variant === 'LIGHT')
                      ? 'Dark covers print the light logo until you add one.'
                      : variant.hint}
                  </span>
                )}
              </div>
              <div className="mt-1.5 flex items-center justify-between gap-2 text-xs">
                <label className="relative cursor-pointer rounded-sm text-sky-600 hover:underline dark:text-sky-400 has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-primary-500">
                  {mark ? 'Replace' : 'Upload'}
                  <input
                    type="file"
                    accept={LOGO_TYPES.join(',')}
                    className="sr-only"
                    aria-label={`Upload logo ${variant.label.toLowerCase()}`}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) choose(variant.key, file);
                      e.target.value = '';
                    }}
                  />
                </label>
                {mark && (
                  <button
                    type="button"
                    className="text-neutral-500 hover:text-red-700 dark:text-neutral-400 dark:hover:text-red-400"
                    onClick={() => onChange(logos.filter((l) => l.variant !== variant.key))}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {error && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</p>}
    </section>
  );
};

export const VoiceFields = ({
  voice,
  editable = true,
  onChange
}: {
  voice: BrandVoiceState | null;
  editable?: boolean;
  onChange: (voice: BrandVoiceState | null) => void;
}) => {
  const set = (patch: Partial<BrandVoiceState>) => onChange({ ...(voice ?? {}), ...patch });
  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-sm font-medium text-neutral-900 dark:text-white">Voice</h3>
        <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
          {editable
            ? 'Writing preferences for Cara.'
            : 'Only an admin can change the agency-wide writing voice in the default design.'}
        </p>
      </div>
      <Field label="Words we use">
        <Textarea
          disabled={!editable}
          rows={3}
          maxLength={1000}
          value={voice?.terminology ?? ''}
          placeholder="Say “client”, not “customer”. Say “renewal review”, not “audit”."
          onChange={(e) => set({ terminology: e.target.value })}
        />
      </Field>
      <Field label="Tone">
        <Textarea
          disabled={!editable}
          rows={3}
          maxLength={1000}
          value={voice?.tone ?? ''}
          placeholder="Warm and direct. Short sentences. No jargon without a plain-English gloss."
          onChange={(e) => set({ tone: e.target.value })}
        />
      </Field>
      <Field label="Never say">
        <Textarea
          disabled={!editable}
          rows={2}
          value={voice?.avoid ?? ''}
          placeholder="guarantee, cheapest, best in class"
          onChange={(e) => set({ avoid: e.target.value })}
        />
      </Field>
    </section>
  );
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <UIField className="mt-2">
    <Label className={LABEL}>{label}</Label>
    {children}
  </UIField>
);

const LABEL = 'text-xs/5 font-medium text-neutral-700 sm:text-xs/5 dark:text-neutral-300';

const Choice = ({
  value,
  disabled,
  onChange,
  options
}: {
  value: string | number;
  disabled?: boolean;
  onChange: (value: string) => void;
  options: { value: string | number; label: string }[];
}) => (
  <Select value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
    {options.map((option) => (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    ))}
  </Select>
);

const Toggle = ({
  label,
  checked,
  disabled,
  onChange
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <CheckboxField disabled={disabled} className="gap-x-2 sm:gap-x-2">
    <Checkbox color="sky" checked={checked} onChange={onChange} />
    <Label className={LABEL}>{label}</Label>
  </CheckboxField>
);

const PartSwitch = ({
  checked,
  onChange
}: {
  checked: boolean;
  onChange: (on: boolean) => void;
}) => (
  <SwitchField className="mt-2 gap-x-3">
    <Label className={LABEL}>Apply to documents</Label>
    <Switch color="sky" checked={checked} onChange={onChange} />
  </SwitchField>
);

export const TypefaceFields = ({
  typefaces,
  bodyTypeface,
  headingTypeface,
  onChange
}: {
  typefaces: TypefaceOption[];
  bodyTypeface: string;
  headingTypeface: string;
  onChange: (next: { bodyTypeface: string; headingTypeface: string }) => void;
}) => (
  <section>
    <h2 className="text-sm font-medium text-neutral-900 dark:text-white">Typefaces</h2>
    <div className="mt-2 flex flex-col">
      {(
        [
          [
            'Headings',
            headingTypeface,
            (v: string) => onChange({ bodyTypeface, headingTypeface: v })
          ],
          ['Body text', bodyTypeface, (v: string) => onChange({ bodyTypeface: v, headingTypeface })]
        ] as const
      ).map(([label, value, set]) => (
        <Field key={label} label={label}>
          <Choice
            value={value}
            onChange={set}
            options={typefaces.map((t) => ({
              value: t.family,
              label: t.metricAlias ? `${t.family} (matches ${t.metricAlias})` : t.family
            }))}
          />
        </Field>
      ))}
    </div>
  </section>
);
